import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link} from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import {useCart} from '../../context/CartContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './DetailProduit.css';

const API_URL = import.meta.env.VITE_API_URL;

const hasValidDataSheet = (dataSheetPath) => {
    if (typeof dataSheetPath !== 'string') return false;
    const normalizedPath = dataSheetPath.trim();
    if (!normalizedPath) return false;

    const loweredPath = normalizedPath.toLowerCase();
    return loweredPath !== 'null' && loweredPath !== 'undefined';
};

const DetailProduit = ({userType, isAuthenticated}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {addToCart} = useCart();

    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (id) {
            fetchProduitDetails();
        }
    }, [id]);

    const fetchProduitDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/produits/${id}`);
            setProduit(response.data);
            setError(null);
        } catch (err) {
            console.error('Erreur lors de la récupération du produit:', err);
            setError('Impossible de trouver ce produit.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = () => {
        if (!produit) return;

        const productToCart = {
            ...produit,
            _id: produit._id,
            nom: produit.nomProduit,
            prix: produit.prix,
            image: produit.imageProduit
        };

        addToCart(productToCart);

        Swal.fire({
            title: 'Ajouté !',
            text: `${produit.nomProduit} a été ajouté à votre panier.`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    if (loading) return <div className="loading-container"><LoadingSpinner message="Chargement des détails..." /></div>;

    if (error || !produit) {
        return (
            <div className="error-container">
                <h2>{error || 'Produit non trouvé'}</h2>
                <button className="btn-retour" onClick={() => navigate('/produits')}>Retour aux produits</button>
            </div>
        );
    }

    const imagePath = `${API_URL.replace('/api', '')}${produit.imageProduit}`;
    const canShowDataSheet = hasValidDataSheet(produit.dataSheet);
    const dataSheetPath = canShowDataSheet ? produit.dataSheet.trim() : '';

    return (
        <div className="detail-produit-container">
            <div className="container">
                {/* Fil d'Ariane */}
                <div className="breadcrumb">
                    <Link to="/home">Accueil</Link>
                    <span className="breadcrumb-separator">/</span>
                    <Link to="/produits">Produits</Link>
                    <span className="breadcrumb-separator">/</span>
                    <span>{produit.nomProduit}</span>
                </div>

                <div className="detail-p-grid">
                    {/* Côte Image */}
                    <div className="detail-p-image-side">
                        <img
                            src={imagePath}
                            alt={produit.nomProduit}
                            className="detail-p-image"
                            onError={(e) => {e.target.src = 'https://via.placeholder.com/600x600?text=Image+non+disponible';}}
                        />
                    </div>

                    {/* Côte Infos */}
                    <div className="detail-p-info-side">
                        <div className="detail-p-header">
                            <span className="detail-p-marque">{produit.marque?.name}</span>
                            <h1 className="detail-p-title">{produit.nomProduit}</h1>
                            <div className="detail-p-badges">
                                <span className="p-badge p-badge-type">
                                    <i className="fas fa-tag"></i> {produit.typeProduit?.label}
                                </span>
                                {produit.reference && (
                                    <span className="p-badge p-badge-ref">
                                        Ref: {produit.reference}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="detail-p-section">
                            <h3>Description</h3>
                            <p className="detail-p-description">{produit.description}</p>
                        </div>

                        <div className="detail-p-actions">
                            {canShowDataSheet && (
                                <a
                                    href={`${API_URL.replace('/api', '')}${dataSheetPath}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-datasheet"
                                >
                                    <i className="fas fa-file-pdf"></i>
                                    Fiche Technique (PDF)
                                </a>
                            )}

                            {isAuthenticated && userType === 'entreprise' && (
                                <div className="purchase-box">
                                    <div className="price-display">
                                        <span className="price-ht">{(produit.prix || 0).toLocaleString()} <small>DT HT</small></span>
                                        <span className="price-tva">+19% TVA</span>
                                    </div>
                                    <button className="btn-add-cart" onClick={handleAddToCart}>
                                        <i className="fas fa-shopping-cart"></i>
                                        Ajouter au panier
                                    </button>
                                </div>
                            )}

                            {!isAuthenticated && (
                                <div className="purchase-box" style={{justifyContent: 'center'}}>
                                    <p style={{margin: 0, color: '#64748b', fontSize: '0.9rem'}}>
                                        <Link to="/login" style={{color: '#1e3a5f', fontWeight: 'bold'}}>Connectez-vous</Link> pour voir les prix et commander.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailProduit;
